import * as React from 'react';
import FaChevronCircleRight from 'react-icons/fa/chevron-circle-right';
import FaPhone from 'react-icons/fa/phone';
import {find, flow, forEach, startCase, get} from 'lodash';
import {Col, Row} from 'react-bootstrap';
import {IChannel, IPremium, IProduct} from '../../../../data/api/models';
import {FormattedNumber} from 'react-intl';
import {connect} from 'react-redux';
import SaveQuoteModal from '../../components/SaveQuoteModal';
import {IQuoteState} from '../../../../services/quote/models';
import SidebarQuoteSummary from './components/SidebarQuoteSummary';
import BenefitsSection from '../../components/BenefitsSection';
import {IStore} from '../../../../redux/IStore';
import {reduxForm} from 'redux-form';
import SecurePaymentSvg from './components/SecurePaymentSvg/SecurePaymentSvg';
import {withRouter} from 'react-router';

interface IQuoteSidebarProps {
    quote: IQuoteState;
    product: IProduct;
    channel: IChannel;
    location: any;
    fireEmail: any;
}

class QuoteSidebar extends React.Component<IQuoteSidebarProps> {

    private get isAdditionalCover() {
        const {quote} = this.props;
        const STEP = 'additionalCover';
        const isAdditionalCover = find(quote.application.steps, (element) => element === STEP);
        return !!isAdditionalCover;
    }

    private get isSelectCover() {
        const {location} = this.props;
        const targetUrl = '/scheme-choice';
        return targetUrl === location.pathname;
    }

    private renderYourQuote = () => {
        const {quote, channel, fireEmail} = this.props;

        const quotes = quote.quotes;
        const schemeId = quote.application.schemeId;
        const isAgg = channel.channelType === 'AGG';

        if (Object.keys(quotes).length === 0) {
            return null;
        }

        let premium: IPremium = null;

        forEach(quotes, (tripTypes) => {
            forEach(tripTypes, (quotedPremium) => {
                if (!quotedPremium.scheme) {
                    return null;
                }

                if (quotedPremium.scheme.id === schemeId) {
                    premium = quotedPremium;
                }
            });
        });

        const customClassList = ['tpl-b2c', 'mpt-b2c'];
        const isOnList = customClassList.includes(channel.key);
        const tripType = premium && premium.information && premium.information.tripType === 'single' ? 'Single Trip'
            : 'Annual Multi-trip';
        const infoContainerClass = ['info-container', isOnList ? 'info-container--top' : ''].join(' ');
        const renderChevronCircle = !['fps-b2c', 'fpr-b2c', 'npo-b2c', 'npor-b2c', 'cspa-b2c',
            'cspar-b2c', 'css-b2c', 'cssr-b2c', 'csis-b2c', 'tpl-b2c', 'camc-b2c'].includes(channel.key) ?
            <FaChevronCircleRight/> : null;
        const infoTitleClass = ['info-title', isOnList ? 'info-title--noBorder' : ''].join(' ');

        return (
            <div>
                <div className={`sidebar-quote-price ${infoContainerClass}`}>
                    <div className={infoTitleClass}>
                        {renderChevronCircle}
                        {quote.complete ? 'Your Policy' : 'Your Quote'}
                    </div>
                    {isAgg && (
                        <p className="info-product-cover-level">
                            {tripType} {startCase(premium && premium.scheme.coverLevel.replace('_', ' '))}
                        </p>
                    )}
                    {premium && (
                        <div className="quote-main-details">
                            <h1>
                                <FormattedNumber
                                    value={premium.gross}
                                    style="currency"
                                    currency={premium.currency}
                                />
                            </h1>
                            <h5>
                                {quote.complete ? ('Total Paid') : ('Total Price')}
                            </h5>
                            <p>inc. Insurance Premium Tax (IPT) if applicable</p>
                        </div>
                    )}
                    {!quote.complete && !isAgg && !this.isSelectCover && (
                        <Row>
                            <Col sm={12}>
                                <SaveQuoteModal {...this.props} fireEmail={fireEmail}/>
                            </Col>
                        </Row>
                    )}
                </div>
            </div>
        );
    }

    private renderQuoteBenefits = () => {
        const {channel} = this.props;

        if (channel.channelType === 'AGG') {
            return (
                <BenefitsSection/>
            );
        }

        return null;
    }

    private renderQuoteSummary = () => {
        const {quote, product, channel} = this.props;
        const quotes = quote.quotes;
        const schemeId = quote.application.schemeId;

        if (!quotes) {
            return null;
        }

        let premium: IPremium = null;

        forEach(quotes, (tripTypes, level) => {
            forEach(tripTypes, (quotedPremium) => {
                if (!quotedPremium) {
                    return null;
                }

                if (quotedPremium.scheme.id === schemeId) {
                    premium = quotedPremium;
                }
            });
        });

        if (quote.complete) {
            return null;
        }

        return (
            <div>
                <SidebarQuoteSummary
                    product={product}
                    channel={channel}
                    isCoverChoice={this.isCoverChoice}
                    isAdditionalCover={this.isAdditionalCover}
                />
            </div>
        );
    }

    private renderLink = (url, text) => {
        return (
            <a
                className="info-links"
                href={url}
                target="_blank"
            >
                {text}
            </a>
        );
    }

    private renderInfoContainer = () => {
        const {channel} = this.props;

        const policyDocument = find(channel.productDocuments, (document) => {
            return document.shortName === 'Evidence of Cover';
        });
        const TERMS_OF_BUSINESS = 'Terms of Business';
        const aiosDocument = find(channel.productDocuments, (document) => {
            return document.shortName === TERMS_OF_BUSINESS;
        });
        const aboutUsUrl = aiosDocument ? aiosDocument.filePath : '';

        return (
            <div className="info-container">
                <div className="info-title">
                    {!['fps-b2c', 'fpr-b2c', 'npo-b2c', 'npor-b2c', 'cspa-b2c',
                        'cspar-b2c', 'css-b2c', 'cssr-b2c', 'csis-b2c', 'tpl-b2c', 'camc-b2c']
                        .includes(channel.key) && (
                        <FaChevronCircleRight/>
                    )}
                    Information
                </div>
                <Row>
                    <Col sm={6} md={12} xs={6}>
                        {policyDocument && (
                            this.renderLink(
                                policyDocument ? policyDocument.filePath : '',
                                channel.key === 'camc-b2c' ||
                                channel.key === 'tpl-b2c' ||
                                channel.key === 'csis-b2c' ? (
                                    'Policy Wording'
                                ) : (
                                    policyDocument ? policyDocument.shortName : 'Insurance Policy'
                                ),
                            )
                        )}
                        {channel.key === 'csis-b2c' || channel.key === 'npo-b2c' || channel.key === 'cspa-b2c' && (
                            this.renderLink(
                                get(channel, 'urls.privacy', ''),
                                'Privacy Policy',
                            )
                        )}
                        {channel.key !== 'csis-b2c' && channel.key !== 'npo-b2c' && channel.key !== 'cspa-b2c' && (
                            get(channel, 'urls.privacyCookies', false) && (
                                this.renderLink(
                                    get(channel, 'urls.privacyCookies', ''),
                                    'Privacy Policy',
                                )
                            )
                        )}
                        {aboutUsUrl && (
                            this.renderLink(
                                aboutUsUrl,
                                'Terms of Business',
                            )
                        )}
                        {(channel.key !== 'camc-b2c' && channel.key !== 'tpl-b2c') && (
                            get(channel, 'urls.privacyCookies', false) && (
                                this.renderLink(
                                    get(channel, 'urls.privacyCookies', ''),
                                    'Cookie policy',
                                )
                            )
                        )}
                        {channel.key === 'camc-b2c' && (
                            this.renderLink(
                                'https://www.pjhayman.com/cookie-policy/',
                                'Cookie policy',
                            )
                        )}
                        <div>
                            {get(channel, 'urls.faq', false) && (
                                this.renderLink(
                                    get(channel, 'urls.faq', ''),
                                    'Frequently Asked Questions',
                                )
                            )}
                            {channel.key !== 'camc-b2c' && channel.key !== 'npo-b2c' && (
                                get(channel, 'urls.keyFeatures', false) && (
                                    this.renderLink(
                                        get(channel, 'urls.keyFeatures', ''),
                                        'Key features',
                                    )
                                )
                            )}
                            {get(channel, 'urls.aboutUs', false) && (
                                this.renderLink(
                                    get(channel, 'urls.aboutUs', ''),
                                    'About Us',
                                )
                            )}
                            {channel.key !== 'camc-b2c' && (
                                get(channel, 'urls.contactUs', false) && (
                                    this.renderLink(
                                        get(channel, 'urls.contactUs', ''),
                                        'Contact',
                                    )
                                )
                            )}
                        </div>
                    </Col>
                    <Col md={12} sm={6} xs={6}>
                        <Row className="secure-payment-container">
                            {channel.key === 'camc-b2c' ? (
                                <Col sm={12}>Buy With Confidence</Col>
                            ) : (
                                <>
                                    <Col sm={6}>
                                        Buy With Confidence
                                    </Col>
                                    <Col sm={6}>
                                        <SecurePaymentSvg/>
                                    </Col>
                                </>
                            )}
                        </Row>
                        <div className="payment-options">
                            <img src={require('./img/cc1.jpg')} alt="card"/>
                            <img src={require('./img/cc2.jpg')} alt="card"/>
                            <img src={require('./img/cc3.jpg')} alt="card"/>
                            <img src={require('./img/cc4.jpg')} alt="card"/>
                        </div>
                    </Col>
                </Row>
            </div>
        );
    }

    private renderPhoneAndOpeningHours = () => {
        const {channel} = this.props;

        return (
            <div className="phone-and-opening-hours">
                <div className="icon">
                    <FaPhone fill={'#FFFFFF'}/>
                </div>
                <a href={`tel:${channel.phone}`} className="phone">
                    {channel.phone}
                </a>
                <div className="opening-hours">
                    {channel.openingHours}
                </div>
            </div>
        );
    }

    public render() {
        return (
            <div className="quote-sidebar">
                {this.renderYourQuote()}
                {this.renderQuoteSummary()}
                {this.renderQuoteBenefits()}
                {this.renderInfoContainer()}
                {this.renderPhoneAndOpeningHours()}
            </div>
        );
    }
}

export default flow([
    reduxForm({
        form: 'quote-sidebar',
        destroyOnUnmount: false,
        forceUnregisterOnUnmount: true,
    }),
    connect(
        (state: IStore) => ({
            quote: state.quote,
            channel: state.branding.channel,
        }),
    ),
    withRouter,
])(QuoteSidebar);



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/app/scenes/Quote/components/QuoteSidebar/index.tsx